import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  useToast,
} from '@/components/ui';
import { ApiError, request } from '@/lib/api';
import type { InventoryItem } from '@/lib/schemas';
import { INVENTORY_QUERY_KEY } from './inventory-api';

const PRICE = /^\d{1,10}(\.\d{1,2})?$/;

/**
 * עלות קנייה של פריט — מה שהופך את דו"ח הרווחיות ממשוער לאמיתי.
 * מחרוזת עשרונית ולא number, כמו במחירון; פסיק עשרוני מתקבל.
 */
export function CostDialog({ item, onClose }: { item: InventoryItem; onClose: () => void }) {
  const { t } = useTranslation(['inventory', 'common']);
  const qc = useQueryClient();
  const toast = useToast();
  const [value, setValue] = useState(item.unitCost ?? '');
  const normalized = value.trim().replace(',', '.');
  const valid = normalized === '' || PRICE.test(normalized);

  const save = useMutation({
    mutationFn: () =>
      request(`/inventory/${item.id}/cost`, { method: 'PATCH', body: { unitCost: normalized === '' ? null : normalized } }),
    onSuccess: async () => {
      toast.success(t('cost.saved'), item.name);
      await qc.invalidateQueries({ queryKey: [INVENTORY_QUERY_KEY] });
      onClose();
    },
    onError: (e) => toast.error(t('common:errors.saveFailed'), e instanceof ApiError ? e.message : undefined),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent aria-describedby="cost-desc">
        <DialogHeader>
          <DialogTitle>{t('cost.title', { name: item.name })}</DialogTitle>
          <DialogDescription id="cost-desc">
            {t('cost.description')}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) save.mutate();
          }}
        >
          <DialogBody>
            <Field label={t('cost.label')} htmlFor="unit-cost" error={valid ? undefined : t('cost.invalid')} hint={t('cost.hint')}>
              <Input
                id="unit-cost"
                autoFocus
                type="text"
                inputMode="decimal"
                className="ltr-inline tabular text-start"
                value={value}
                invalid={!valid}
                onChange={(e) => setValue(e.target.value)}
              />
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit" disabled={!valid} loading={save.isPending}>
              {t('common:actions.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
